import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Input, InputNumber, Checkbox } from 'antd';
import { appMessage as message } from '../../utils/appMessage';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, DeleteOutlined, CameraOutlined } from '@ant-design/icons';

import {
  FONT_SIZE, FONT_WEIGHT, LINE_HEIGHT, RADIUS, SHADOW, SPACING,
} from '../../constants/constants';
import type { ThemeTokens } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { formatPrice } from '../../utils/format';
import { searchRegistrableCards } from '../../api/api';
import {
  RegistrableGameType, RegistrableSearchResult,
  REGISTER_INIT_STOCK, REGISTER_PLACEHOLDER_PRICE,
  registrationCode as codeOf, candidateName, candidateExpansionName,
} from '../../types/productRegistration';
import { AppButton } from '../common/AppButton';
import { AppTag } from '../common/AppTag';
import { CardThumb } from '../buyback/CardThumb';
import { RegisterEmptyState } from '../registerEmptyState/RegisterEmptyState';
import { ProductRegisterTable } from '../productRegisterTable/ProductRegisterTable';
import { RegistrationRequestModal } from '../modal/RegistrationRequestModal';

export interface QueueRegisterSummary {
  productCodes: string[];
  stockByCode:  Record<string, number>;
  count:        number;
  totalStock:   number;
}

interface Props {
  gameType:        RegistrableGameType;
  /** 다른 등록 잡이 진행 중이면 true — "스토어에 등록" 버튼을 잠근다(중복 등록 방지). */
  busy?:           boolean;
  onRegisterClick: (summary: QueueRegisterSummary) => void;
}

const ellipsisText: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

// ─── 검색 결과 표의 뒤 컬럼(공유 이미지·레어도 뒤) ───────────────────────────

function tailColumns(
  t: ThemeTokens,
  queued: Set<string>,
  onToggle: (r: RegistrableSearchResult) => void,
): ColumnsType<RegistrableSearchResult> {
  return [
    {
      title: '등록될 상품명', key: 'name', ellipsis: true,
      render: (_, r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ ...ellipsisText, fontSize: FONT_SIZE.sm, color: t.textPrimary }}>{r.previewName}</div>
          <div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>{r.candidate.cardNumber}</div>
        </div>
      ),
    },
    {
      title: '확장팩', key: 'expansion', width: 150, ellipsis: true,
      render: (_, r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ ...ellipsisText, fontSize: FONT_SIZE.sm, color: t.textSecondary }}>{candidateExpansionName(r)}</div>
          <div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>{r.candidate.expansionCode}</div>
        </div>
      ),
    },
    {
      title: '담기', key: 'add', width: 110, align: 'right',
      render: (_, r) => {
        if (r.alreadyRegistered) {
          return (
            <AppButton variant="default" size="sm" disabled disabledReason="이미 스토어에 등록된 상품입니다">
              이미 등록됨
            </AppButton>
          );
        }
        const inQueue = queued.has(codeOf(r));
        return (
          <AppButton variant={inQueue ? 'text' : 'primary'} size="sm" onClick={() => onToggle(r)}>
            {inQueue ? '✓ 담김' : '+ 담기'}
          </AppButton>
        );
      },
    },
  ];
}

// ─── SearchRegisterPanel ─────────────────────────────────────────────────────

/**
 * "검색 개별 등록" 탭(2026-08-31, "상품 등록 개선안"). 카드명/번호로 검색해 "등록 대기
 * 목록"에 담고, 재고를 입력한 뒤 확인 모달을 거쳐 한 번에 등록한다. 기본으로 이미 등록된
 * 카드는 결과에서 숨기고(등록 가능만 보기), 레어도는 색 배지로 구분한다. 검색해도 안 나오는
 * 카드는 결과 없음 빈 상태 안에서 사진으로 등록 요청한다.
 *
 * 게임 전환·등록 완료 시 상태 초기화는 부모(ProductRegistrationPage)가 key로 리마운트해
 * 처리한다 — 이 패널은 자기 상태만 신경 쓴다.
 */
export const SearchRegisterPanel: React.FC<Props> = ({ gameType, busy = false, onRegisterClick }) => {
  const t = useThemeTokens();

  const [query, setQuery]             = useState('');
  const [results, setResults]         = useState<RegistrableSearchResult[]>([]);
  const [searching, setSearching]     = useState(false);
  const [onlyNew, setOnlyNew]         = useState(true);
  const [queue, setQueue]             = useState<RegistrableSearchResult[]>([]);
  const [stockByCode, setStockByCode] = useState<Record<string, number>>({});
  const [bulkStock, setBulkStock]     = useState(REGISTER_INIT_STOCK);
  const [photoOpen, setPhotoOpen]     = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback((value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(() => {
      setSearching(true);
      searchRegistrableCards(gameType, value.trim())
        .then(setResults)
        .catch(() => message.error('검색에 실패했습니다'))
        .finally(() => setSearching(false));
    }, 350);
  }, [gameType]);

  const queued = useMemo(() => new Set(queue.map(codeOf)), [queue]);
  const visible = useMemo(
    () => (onlyNew ? results.filter((r) => !r.alreadyRegistered) : results),
    [results, onlyNew],
  );
  const hiddenCount = results.length - visible.length;

  const toggleQueue = useCallback((r: RegistrableSearchResult) => {
    setQueue((prev) => prev.some((p) => codeOf(p) === codeOf(r))
      ? prev.filter((p) => codeOf(p) !== codeOf(r))
      : [...prev, r]);
  }, []);

  const addAllVisible = useCallback(() => {
    setQueue((prev) => {
      const have = new Set(prev.map(codeOf));
      const toAdd = visible.filter((r) => !r.alreadyRegistered && !have.has(codeOf(r)));
      return toAdd.length ? [...prev, ...toAdd] : prev;
    });
  }, [visible]);

  const removeFromQueue = useCallback((code: string) => {
    setQueue((prev) => prev.filter((r) => codeOf(r) !== code));
    setStockByCode((prev) => {
      const { [code]: _drop, ...rest } = prev;
      return rest;
    });
  }, []);

  const stockOf = useCallback(
    (code: string) => stockByCode[code] ?? bulkStock,
    [stockByCode, bulkStock],
  );
  const totalStock = useMemo(
    () => queue.reduce((sum, r) => sum + stockOf(codeOf(r)), 0),
    [queue, stockOf],
  );

  const applyBulkStock = useCallback(() => {
    setStockByCode(Object.fromEntries(queue.map((r) => [codeOf(r), bulkStock])));
  }, [queue, bulkStock]);

  const addableCount = visible.filter((r) => !r.alreadyRegistered && !queued.has(codeOf(r))).length;

  const columns = useMemo(
    () => tailColumns(t, queued, toggleQueue),
    [t, queued, toggleQueue],
  );

  const panelBox: React.CSSProperties = {
    background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: RADIUS.lg, boxShadow: SHADOW.card,
  };
  const q = query.trim();

  return (
    <div style={{ display: 'flex', gap: SPACING.md, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {/* 검색 열 */}
      <div style={{ flex: '1 1 420px', minWidth: 420, display: 'flex', flexDirection: 'column', gap: SPACING.md }}>
        <div style={{ ...panelBox, overflow: 'hidden' }}>
          <div style={{
            padding: `${SPACING.sm}px ${SPACING.lg}px`, borderBottom: `1px solid ${t.border}`,
            display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap',
          }}>
            <Input
              placeholder="카드명 또는 카드번호 검색"
              prefix={<SearchOutlined style={{ color: t.textMuted }} />}
              value={query}
              onChange={(e) => handleSearchChange(e.target.value)}
              allowClear
              style={{ flex: 1, minWidth: 220 }}
            />
            <Checkbox checked={onlyNew} onChange={(e) => setOnlyNew(e.target.checked)} style={{ fontSize: FONT_SIZE.sm, flex: 'none' }}>
              등록 가능만 보기
            </Checkbox>
          </div>

          {/* 검색 결과 있음 — 표시할 게 없을 수도 있다("등록 가능만 보기"로 전부 걸러진 경우).
              그 경우에도 "이미 등록된 N건 보기" 링크는 반드시 남겨야 한다(표 블록 안에 두면
              표가 안 뜰 때 링크도 같이 사라져 패널이 하얗게 빈다 — 2026-08-31 버그 수정). */}
          {results.length > 0 && (
            <>
              {visible.length > 0 ? (
                <>
                  <div style={{
                    padding: `${SPACING.xs}px ${SPACING.lg}px`, background: t.bgRaised, borderBottom: `1px solid ${t.border}`,
                    display: 'flex', alignItems: 'center', gap: SPACING.sm,
                  }}>
                    <span style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
                      {onlyNew
                        ? `등록 가능 ${visible.length}건 · 이미 등록된 ${hiddenCount}건 숨김`
                        : `검색 결과 ${results.length}건`}
                    </span>
                    <span style={{ marginLeft: 'auto' }}>
                      <AppButton
                        variant="default" size="sm"
                        disabled={addableCount === 0}
                        disabledReason="담을 수 있는 카드가 없습니다"
                        onClick={addAllVisible}
                      >
                        보이는 {addableCount}건 모두 담기
                      </AppButton>
                    </span>
                  </div>
                  <ProductRegisterTable
                    rows={visible}
                    columns={columns}
                    loading={searching}
                    pagination={{ pageSize: 10, hideOnSinglePage: true }}
                  />
                </>
              ) : (
                <RegisterEmptyState
                  size="sm"
                  title="검색 결과가 전부 이미 등록되어 있습니다"
                  description={`검색된 ${results.length}건이 모두 스토어에 등록된 상품입니다.`}
                />
              )}
              {onlyNew && hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={() => setOnlyNew(false)}
                  style={{
                    width: '100%', textAlign: 'left', cursor: 'pointer',
                    padding: `${SPACING.xs}px ${SPACING.lg}px`, background: t.bgRaised,
                    border: 'none', borderTop: `1px solid ${t.border}`,
                    color: t.accent, fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.semibold,
                  }}
                >
                  이미 등록된 {hiddenCount}건 보기
                </button>
              )}
            </>
          )}

          {/* 빈 상태 — 검색어 없음 */}
          {q === '' && (
            <RegisterEmptyState
              title="카드명이나 카드번호로 검색하세요"
              description="여러 번 검색해서 오른쪽 대기 목록에 계속 담을 수 있습니다"
            />
          )}

          {/* 빈 상태 — 검색 결과 없음 → 사진 등록 요청 */}
          {q !== '' && !searching && results.length === 0 && (
            <RegisterEmptyState
              size="sm"
              title={`"${q}" 검색 결과가 없습니다`}
              description={<>
                PriceHub 카탈로그에 아직 없는 카드일 수 있습니다.<br />
                사진을 보내면 관리자가 확인 후 카탈로그에 등록합니다.
              </>}
              action={(
                <AppButton variant="primary" icon={<CameraOutlined />} onClick={() => setPhotoOpen(true)}>
                  사진으로 등록 요청
                </AppButton>
              )}
              footnote="보낸 사진 요청은 반영 로그에서 확인할 수 있습니다"
            />
          )}
        </div>
      </div>

      {/* 등록 대기 목록 */}
      {queue.length === 0 ? (
        <div style={{
          width: 230, flex: 'none', position: 'sticky', top: SPACING.lg,
          background: t.bgSurface, border: `1px dashed ${t.borderSubtle}`, borderRadius: RADIUS.lg,
          padding: `${SPACING.lg}px ${SPACING.md}px`,
        }}>
          <div style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary }}>등록 대기 목록</div>
          <div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted, marginTop: SPACING.xs, lineHeight: LINE_HEIGHT.normal }}>
            담은 카드가 여기 쌓입니다. 재고를 입력한 뒤 한 번에 등록합니다.
          </div>
        </div>
      ) : (
        <div
          className="register-queue-panel"
          style={{
            width: 430, flex: 'none', position: 'sticky', top: SPACING.lg,
            background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: RADIUS.lg,
            boxShadow: SHADOW.card, overflow: 'hidden',
          }}
        >
          <div style={{
            padding: `${SPACING.sm}px ${SPACING.md}px`, borderBottom: `1px solid ${t.border}`,
            display: 'flex', alignItems: 'center', gap: SPACING.sm,
          }}>
            <span style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary }}>
              등록 대기 {queue.length}건
            </span>
            <AppButton
              variant="link" size="sm"
              style={{ marginLeft: 'auto', height: 'auto', padding: 0 }}
              onClick={() => { setQueue([]); setStockByCode({}); }}
            >
              전체 비우기
            </AppButton>
          </div>

          <div style={{
            padding: `${SPACING.xs}px ${SPACING.md}px`, background: t.bgRaised, borderBottom: `1px solid ${t.border}`,
            display: 'flex', alignItems: 'center', gap: SPACING.xs, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, flex: 'none' }}>재고 일괄</span>
            <InputNumber
              min={1}
              value={bulkStock}
              onChange={(v) => setBulkStock(v ?? 1)}
              size="small"
              style={{ width: 68 }}
            />
            <AppButton variant="default" size="sm" onClick={applyBulkStock}>전체 적용</AppButton>
            <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted, marginLeft: 'auto' }}>개별 수정 가능</span>
          </div>

          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {queue.map((r) => {
              const code = codeOf(r);
              return (
                <div
                  key={code}
                  style={{
                    display: 'grid', gridTemplateColumns: '40px 1fr 84px 28px', alignItems: 'center', gap: SPACING.xs,
                    padding: `${SPACING.xs}px ${SPACING.md}px`, borderBottom: `1px solid ${t.borderSubtle}`,
                  }}
                >
                  <CardThumb imageUrl={r.candidate.imageUrl} alt={candidateName(r)} size={28} preview={false} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xxs, minWidth: 0 }}>
                      <AppTag variant="rarity" code={r.candidate.rarity} />
                      <span style={{ ...ellipsisText, fontSize: FONT_SIZE.xs, color: t.textPrimary }}>
                        {candidateName(r)}
                      </span>
                    </div>
                    <div style={{ ...ellipsisText, fontSize: FONT_SIZE.xs, color: t.textMuted }}>
                      {candidateExpansionName(r)} · {r.candidate.cardNumber}
                    </div>
                  </div>
                  <InputNumber
                    min={1}
                    size="small"
                    value={stockByCode[code] ?? bulkStock}
                    onChange={(v) => setStockByCode((prev) => ({ ...prev, [code]: v ?? 1 }))}
                    style={{ width: 80 }}
                  />
                  <AppButton
                    variant="text" size="sm" iconOnly
                    icon={<DeleteOutlined />}
                    style={{ color: t.danger }}
                    onClick={() => removeFromQueue(code)}
                  />
                </div>
              );
            })}
          </div>

          <div style={{ padding: `${SPACING.sm}px ${SPACING.md}px`, background: t.bgRaised, borderTop: `1px solid ${t.border}` }}>
            <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, lineHeight: LINE_HEIGHT.normal }}>
              판매중지 · 가격 {formatPrice(REGISTER_PLACEHOLDER_PRICE)}으로 생성 · 총 재고 {totalStock.toLocaleString()}장
            </div>
            <AppButton
              variant="primary"
              style={{ width: '100%', marginTop: SPACING.xs }}
              disabled={busy}
              disabledReason="다른 상품 등록 작업이 진행 중입니다"
              onClick={() => onRegisterClick({
                productCodes: queue.map(codeOf),
                stockByCode: Object.fromEntries(queue.map((r) => [codeOf(r), stockOf(codeOf(r))])),
                count: queue.length,
                totalStock,
              })}
            >
              {queue.length}건 스토어에 등록
            </AppButton>
          </div>
        </div>
      )}

      <RegistrationRequestModal open={photoOpen} gameType={gameType} onClose={() => setPhotoOpen(false)} />
    </div>
  );
};
